import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  Output, EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'cipx-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule
  ],
})
export class CIPXSearchableSelectComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() label!: string;
  @Input() placeholder = 'Search...';
  @Input() options: any[] = [];
  @Input() valueKey = 'id';
  @Input() labelKey = 'label';
  @Input() multiple = true;
  @Input() appearance: 'fill' | 'outline' = 'fill';
  @Input() sortAlphabetically = true;

  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild('select') select!: MatSelect;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchControl = new FormControl('');
  filteredOptions: any[] = [];
  hiddenSelectedOptions: any[] = [];
  private _onDestroy = new Subject<void>();

  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterOptions();
      });

    // Handle form control changes to keep filtered options up to date with selections
    this.formGroup.get(this.controlName)?.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterOptions();
      });

    this.filterOptions();
  }

  ngOnChanges(changes: any) {
    if (changes.options) {
      this.filterOptions();
    }
    if (changes.formGroup) {
      this.filterOptions();
    }
  }

  ngAfterViewInit() {
    this.select.openedChange.subscribe((opened) => {
      if (opened) {
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
        }, 100);
      } else {
        this.clearSearch();
      }
    });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  filterOptions() {
    if (!this.options) {
      this.filteredOptions = [];
      return;
    }

    const search = (this.searchControl.value || '').toLowerCase();
    const selectedValues = this.getSelectedValues();

    let filtered;
    if (!search) {
      filtered = [...this.options];
    } else {
      const searchLower = search.toLowerCase();
      filtered = this.options.filter(option => {
        const label = String(option[this.labelKey] || '').toLowerCase();
        return label.includes(searchLower);
      });
    }

    if (this.sortAlphabetically) {
      filtered.sort((a, b) => {
        const isSelectedA = selectedValues.includes(a[this.valueKey]);
        const isSelectedB = selectedValues.includes(b[this.valueKey]);

        if (isSelectedA && !isSelectedB) return -1;
        if (!isSelectedA && isSelectedB) return 1;

        const labelA = String(a[this.labelKey] || '').toLowerCase();
        const labelB = String(b[this.labelKey] || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
    } else {
      // Even if alphabetical sorting is off, we still want to show selected at the top if requested
      filtered.sort((a, b) => {
        const isSelectedA = selectedValues.includes(a[this.valueKey]);
        const isSelectedB = selectedValues.includes(b[this.valueKey]);
        if (isSelectedA && !isSelectedB) return -1;
        if (!isSelectedA && isSelectedB) return 1;
        return 0;
      });
    }

    this.filteredOptions = filtered;

    // Keep selected options that are not part of the current filtered list rendered
    // (but hidden) so that Angular Material's mat-select does not drop them from the
    // selection when their mat-option is removed from the DOM during filtering.
    const filteredValues = filtered.map(option => option[this.valueKey]);
    this.hiddenSelectedOptions = this.options.filter(option =>
      selectedValues.includes(option[this.valueKey]) && !filteredValues.includes(option[this.valueKey])
    );
  }

  getSelectedValues(): any[] {
    const control = this.formGroup.get(this.controlName);
    if (!control) return [];
    const value = control.value;
    if (this.multiple) {
      return Array.isArray(value) ? value : [];
    } else {
      return value !== null && value !== undefined ? [value] : [];
    }
  }

  clearSearch(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.searchControl.setValue('');
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.select.close();
      event.stopPropagation();
    }
  }

  onOptionSelected() {
    this.selectionChange.emit(this.formGroup.get(this.controlName)?.value);
    if (this.multiple) {
        // Clear search text so that the list is refreshed automatically
        this.clearSearch();
        setTimeout(() => {
            this.searchInput.nativeElement.focus();
        });
    } else {
        // For single select, it closes automatically, but we should clear search for next time
        this.clearSearch();
    }
  }
}
